import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AbsenceBadge } from '@/components/AbsenceBadge';
import { Avatar } from '@/components/PersonCard';
import { leadBadge } from '@/components/personStyles';
import { directoryPhotoUrl } from '@/lib/directory/useDirectory';
import { ORG_CARD_SHADOW, elbowPath } from '@/lib/orgChartGeometry';

/**
 * 조직도 <b>트리</b> — 직원찾기의 [조직도] 와 인력배치표 › 조직도 › 본사가 함께 쓰는 도식
 * (사용자 지시 2026-10-01: 「회장 → 관리부/기술부 → 본부 → 부서」 그림 그대로, 디자인은 세련되게).
 *
 * <h2>칸 하나가 부서 하나다</h2>
 * 위 반 칸이 부서 이름, 아래 반 칸이 <b>그 부서 사람 전원</b>이다(직급순 — 순서는 서버가 세워 보낸
 * 그대로다, {@code DirectoryService.MEMBER_ORDER}). 사람이 없는 부서는 아래 칸이 비어 있는 채로 선다 —
 * 그림의 빈 칸이 그것이고, 「기술부」 처럼 사람은 없고 가지만 있는 층이 실제로 있다.
 *
 * <h2>자리는 계산으로 정한다</h2>
 * 칸 높이는 사람 수로 미리 안다(한 줄 22px). 그래서 DOM 을 재지 않고 좌표를 전부 셈으로 놓고, 선은
 * 그 좌표로 SVG 에 긋는다 — 재서 그리면 첫 프레임에 선이 없고, 칸이 늘 때마다 선이 한 박자 늦는다.
 *
 * <h2>선은 자식 바로 위에서 꺾인다</h2>
 * 가로줄(버스)은 부모와 자식의 가운데가 아니라 <b>자식 줄 바로 위의 빈 띠</b>에 둔다. 가운데에 두면
 * 칸 높이가 제각각일 때 가로줄이 옆 칸의 글자를 뚫고 지난다(현장 조직도에서 겪었다, 2026-10-01).
 *
 * <h2>가지가 많으면 두 줄로 내린다</h2>
 * 자식이 {@link STACK_MIN} 이상이고 전부 잎이면(그 아래가 없으면) 그림의 「기술 1부 … 7부」 처럼
 * 가운데 척추 하나에 좌우 두 열로 매단다. 일곱 칸을 한 줄로 늘어놓으면 판이 화면 두 배 폭이 된다.
 * 홀수면 마지막 하나는 척추 끝에 가운데로 선다 — 그림의 「기술 7부」 자리다.
 */

export type OrgTreeMember = {
  personnelId: string;
  name: string;
  rank: string | null;
  teamLead: boolean;
  hasPhoto: boolean;
};

export type OrgTreeNode = {
  id: string;
  name: string;
  /** 비어 있거나 목록에 없는 id 면 뿌리로 선다. */
  parentId: string | null;
  members: OrgTreeMember[];
  /** 머리칸을 눌러 고칠 수 있는 칸인지. 부서가 아닌 칸(현장·소속 미지정)은 고칠 것이 없다. */
  editable?: boolean;
  /**
   * 따로 서는 자리(사용자 지시 2026-10-01). {@code TOP} 은 맨 윗줄에 독립해서(감사실 — 현장 판의
   * 총괄소장 자리), {@code SIDE} 는 오른쪽 구석에 세로로(현장준비팀 · 소속 미지정). 없으면 조직 안.
   * 뿌리가 아닌 칸에 적혀 있어도 무시한다 — 부모 아래 서는 칸은 부모가 자리를 정한다.
   */
  slot?: 'TOP' | 'SIDE' | null;
  /**
   * 계산한 자리에서 손으로 옮긴 거리(px · V391). 아래 가지가 함께 따라가고, 선은 옮긴 자리에서 다시
   * 긋는다 — 어디로 끌어도 부모·자식 선이 끊기지 않는다(사용자 지시 2026-10-01 「선은 그대로 가는 형태로」).
   */
  offset?: { dx: number; dy: number };
};

type Drag = {
  id: string;
  startX: number;
  startY: number;
  baseDx: number;
  baseDy: number;
  dx: number;
  dy: number;
};

const NODE_W = 152;
/** 머리칸 한 줄 높이와 위아래 여백. 열 글자가 넘는 이름은 한 줄 더 쓴다. */
const HEADER_LINE = 18;
const HEADER_PAD = 16;
const HEADER_CHARS_PER_LINE = 10;
const ROW_H = 22;
const BODY_PAD = 8;
/** 사람이 없는 부서의 아래 칸 — 그림의 빈 칸. */
const EMPTY_BODY_H = 16;
/** 「+」 줄(고치는 화면에서만). 높이를 셈에 넣어야 선이 그 아래에서 시작한다. */
const ADD_ROW_H = 22;
const H_GAP = 20;
/** 줄과 줄 사이의 띠. 가로줄이 이 한가운데를 지난다. */
const V_GAP = 44;
const STACK_MIN = 5;
const STACK_COL_GAP = 28;
const STACK_ROW_GAP = 12;
const STACK_TOP_GAP = 24;
const FOREST_GAP = 48;
/** 뿌리 여러 그루를 한 줄에 세우는 폭 상한. 넘으면 다음 줄로. */
const FOREST_ROW_MAX = 1500;
/** 오른쪽 구석에 세로로 쌓이는 칸 사이. */
const SIDE_GAP = 16;
/** 맞춤 축소의 바닥. 이보다 작게 줄이면 이름이 안 읽혀서, 그 아래로는 가로 스크롤이 남는다. */
const MIN_SCALE = 0.55;
const FRAME_PAD_X = 8;
/** 손으로 줄이고 키우는 범위. 맞춤 배율과 무관하게 이 사이에서만 움직인다. */
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 1.2;

type Tree = { node: OrgTreeNode; children: Tree[] };
type Placed = { node: OrgTreeNode; depth: number; x: number; y: number; h: number };

export function OrgTreeChart({
  nodes,
  selectedId,
  onPick,
  onEditNode,
  onAddMember,
  onMoveNode,
  prune = false,
  maxHeight,
}: {
  nodes: OrgTreeNode[];
  selectedId: string | null;
  onPick: (personnelId: string) => void;
  /** 있으면 머리칸이 단추가 된다(인력배치표). 직원찾기는 넘기지 않는다 — 찾는 자리에서 고치지 않는다. */
  onEditNode?: (node: OrgTreeNode) => void;
  /** 있으면 칸 아래에 「+」 줄이 선다 — 현장 판의 + 와 같은 자리(사용자 지시 2026-10-01). 사람을 이 부서에 넣는다. */
  onAddMember?: (node: OrgTreeNode) => void;
  /**
   * 있으면 머리칸에 화살표 손잡이가 선다. 그것을 누른 채 끌면 칸(과 아래 가지)이 따라오고, 놓으면
   * 계산한 자리로부터의 거리(px)를 돌려준다 — 부르는 쪽이 저장한다.
   */
  onMoveNode?: (node: OrgTreeNode, dx: number, dy: number) => void;
  /** 사람이 없는 잎을 걷어낸다 — 검색 중에는 걸린 사람이 든 가지만 남긴다. */
  prune?: boolean;
  maxHeight?: string;
}) {
  const addRow = onAddMember !== undefined;

  /** 끌고 있는 칸. 놓기 전까지는 화면에만 있고, 좌표는 매 움직임마다 다시 짠다(칸 서른 개면 가볍다). */
  const [drag, setDrag] = useState<Drag | null>(null);
  const liveNodes = useMemo(
    () => (drag ? nodes.map((node) => (node.id === drag.id ? { ...node, offset: { dx: drag.dx, dy: drag.dy } } : node)) : nodes),
    [nodes, drag],
  );

  const { placed, links, width, height } = useMemo(
    () => layout(buildForest(liveNodes, prune), addRow),
    [liveNodes, prune, addRow],
  );

  /*
    한 화면에 다 나오게 — 판이 칸보다 넓으면 <b>통째로 줄인다</b>(사용자 지시 2026-10-01 「좌우로
    드래그 해야 해? 한 화면에 다 나와야지」). 좌표는 그대로 두고 transform 으로만 줄이므로 선과 칸이
    같이 줄어 어긋나지 않는다. 바닥({@link MIN_SCALE}) 아래로는 글자가 안 읽혀 가로 스크롤을 남긴다.
  */
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = useState(0);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setFrameWidth(frame.clientWidth));
    observer.observe(frame);
    setFrameWidth(frame.clientWidth);
    return () => observer.disconnect();
  }, []);

  const available = Math.max(0, frameWidth - FRAME_PAD_X * 2);
  const fitScale = width > 0 && available > 0 && width > available ? Math.max(MIN_SCALE, available / width) : 1;

  /*
    확대·축소 (사용자 지시 2026-10-02 「드래그해서 확대가 안 돼, 너무 작아」). 맞춤이 기본이고 Ctrl+휠이나
    ＋/－ 를 누르면 맞춤에서 풀려 그 배율로 선다. 판이 칸보다 커지면 스크롤이 생기고, 빈 자리를 누른 채
    끌면 스크롤이 따라온다. 칸 위의 단추(사람·손잡이·머리칸)는 끌기에서 뺀다 — 거기서는 칸이 옮겨진다.
    배율을 바꿀 때는 마우스가 있던 자리(단추면 가운데)가 제자리에 남게 스크롤을 맞춘다.
  */
  const [zoom, setZoom] = useState<number | null>(null);
  const scale = zoom ?? fitScale;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const anchor = useRef<{ x: number; y: number } | null>(null);
  const zoomBy = (factor: number) => setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scaleRef.current * factor)));
  const zoomByRef = useRef(zoomBy);
  zoomByRef.current = zoomBy;

  // 휠 확대. passive 가 아닌 리스너로 붙여야 preventDefault 가 먹는다(React onWheel 은 passive).
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const rect = frame.getBoundingClientRect();
      anchor.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      zoomByRef.current(event.deltaY < 0 ? 1.1 : 1 / 1.1);
    };
    frame.addEventListener('wheel', onWheel, { passive: false });
    return () => frame.removeEventListener('wheel', onWheel);
  }, []);

  const prevScale = useRef(scale);
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const before = prevScale.current;
    prevScale.current = scale;
    if (!frame || before === scale || before === 0) return;
    const ratio = scale / before;
    const ax = anchor.current?.x ?? frame.clientWidth / 2;
    const ay = anchor.current?.y ?? frame.clientHeight / 2;
    anchor.current = null;
    frame.scrollLeft = (frame.scrollLeft + ax) * ratio - ax;
    frame.scrollTop = (frame.scrollTop + ay) * ratio - ay;
  }, [scale]);

  /** 빈 자리를 끌어 스크롤을 민다. 터치는 브라우저가 알아서 굴리므로 마우스만 받는다. */
  const pan = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [panning, setPanning] = useState(false);
  const startPan = (event: React.PointerEvent<HTMLDivElement>) => {
    const frame = frameRef.current;
    if (!frame || event.pointerType !== 'mouse' || event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button')) return;
    frame.setPointerCapture(event.pointerId);
    pan.current = { x: event.clientX, y: event.clientY, left: frame.scrollLeft, top: frame.scrollTop };
    setPanning(true);
  };
  const movePan = (event: React.PointerEvent<HTMLDivElement>) => {
    const frame = frameRef.current;
    if (!frame || !pan.current) return;
    frame.scrollLeft = pan.current.left - (event.clientX - pan.current.x);
    frame.scrollTop = pan.current.top - (event.clientY - pan.current.y);
  };
  const endPan = () => {
    pan.current = null;
    setPanning(false);
  };

  /** 화살표를 누른 순간. 포인터 캡처는 손잡이 단추가 잡으므로 움직임·놓음도 거기로 온다. */
  const startMove = (node: OrgTreeNode, event: React.PointerEvent) => {
    setDrag({
      id: node.id,
      startX: event.clientX,
      startY: event.clientY,
      baseDx: node.offset?.dx ?? 0,
      baseDy: node.offset?.dy ?? 0,
      dx: node.offset?.dx ?? 0,
      dy: node.offset?.dy ?? 0,
    });
  };
  const move = (event: React.PointerEvent) => {
    setDrag((current) =>
      current
        ? {
            ...current,
            // 판이 줄어 있으면 손은 화면 px, 칸은 판 px 다 — 나눠야 손끝을 따라온다.
            dx: current.baseDx + (event.clientX - current.startX) / scale,
            dy: current.baseDy + (event.clientY - current.startY) / scale,
          }
        : current,
    );
  };
  // 바깥에 알리는 일은 setState 갱신 함수 안에서 하지 않는다 — 다른 컴포넌트의 setState 가 렌더 중에 불린다.
  const endMove = (node: OrgTreeNode) => {
    if (drag && drag.id === node.id && onMoveNode) onMoveNode(node, Math.round(drag.dx), Math.round(drag.dy));
    setDrag(null);
  };

  if (placed.length === 0) return null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '2px', padding: `0 ${FRAME_PAD_X}px` }}>
        <button type="button" className="btn-ghost" style={zoomButtonStyle} aria-pressed={zoom === null} onClick={() => setZoom(null)}>
          맞춤
        </button>
        <button type="button" className="btn-ghost" style={zoomButtonStyle} aria-label="축소" onClick={() => zoomBy(1 / ZOOM_STEP)}>
          －
        </button>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '36px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(scale * 100)}%
        </span>
        <button type="button" className="btn-ghost" style={zoomButtonStyle} aria-label="확대" title="Ctrl+휠" onClick={() => zoomBy(ZOOM_STEP)}>
          ＋
        </button>
      </div>

    <div
      ref={frameRef}
      style={{ overflow: 'auto', maxHeight, padding: `12px ${FRAME_PAD_X}px 16px`, cursor: panning ? 'grabbing' : undefined }}
      onPointerDown={startPan}
      onPointerMove={movePan}
      onPointerUp={endPan}
      onPointerCancel={endPan}
    >
      <div style={{ width: `${width * scale}px`, height: `${height * scale}px`, margin: '0 auto' }}>
      <div
        style={{
          position: 'relative',
          width: `${width}px`,
          height: `${height}px`,
          transform: scale === 1 ? undefined : `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <svg
          width={width}
          height={height}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
          aria-hidden
        >
          {links.map((d) => (
            <path key={d} d={d} fill="none" stroke="var(--border-strong)" strokeWidth={1.5} strokeLinecap="round" />
          ))}
        </svg>

        {placed.map((item) => (
          <NodeBox
            key={item.node.id}
            item={item}
            selectedId={selectedId}
            onPick={onPick}
            onEdit={onEditNode && item.node.editable !== false ? () => onEditNode(item.node) : undefined}
            onAdd={onAddMember && item.node.editable !== false ? () => onAddMember(item.node) : undefined}
            moving={drag?.id === item.node.id}
            moveHandlers={
              onMoveNode && item.node.editable !== false
                ? {
                    onPointerDown: (event) => {
                      event.preventDefault();
                      event.currentTarget.setPointerCapture(event.pointerId);
                      startMove(item.node, event);
                    },
                    onPointerMove: (event) => {
                      if (drag?.id === item.node.id) move(event);
                    },
                    onPointerUp: () => endMove(item.node),
                    onPointerCancel: () => setDrag(null),
                  }
                : undefined
            }
          />
        ))}
      </div>
      </div>
    </div>
    </div>
  );
}

const zoomButtonStyle: React.CSSProperties = { fontSize: '12px', padding: '2px 6px', lineHeight: '18px' };

// ── 칸 ───────────────────────────────────────────────────────────────────────

type MoveHandlers = Pick<
  React.DOMAttributes<HTMLButtonElement>,
  'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
>;

function NodeBox({
  item,
  selectedId,
  onPick,
  onEdit,
  onAdd,
  moving = false,
  moveHandlers,
}: {
  item: Placed;
  selectedId: string | null;
  onPick: (personnelId: string) => void;
  onEdit?: () => void;
  onAdd?: () => void;
  /** 지금 끌리는 중 — 그림자를 키워 「들려 있다」 를 보인다. */
  moving?: boolean;
  /** 화살표 손잡이에 붙는 포인터 이벤트. 없으면 손잡이가 안 선다(직원찾기). */
  moveHandlers?: MoveHandlers;
}) {
  const { node, depth, x, y, h } = item;
  const root = depth === 0;
  const headerH = headerLines(node.name) * HEADER_LINE + HEADER_PAD;

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    width: '100%',
    minHeight: `${headerH}px`,
    // 오른쪽은 화살표 손잡이 자리만큼 비운다 — 이름이 손잡이 밑으로 들어가지 않게.
    padding: moveHandlers ? '0 24px 0 10px' : '0 10px',
    fontSize: '13px',
    fontWeight: 700,
    lineHeight: `${HEADER_LINE}px`,
    textAlign: 'center',
    wordBreak: 'keep-all',
    background: root ? 'var(--accent)' : depth === 1 ? 'var(--accent-bg)' : 'var(--bg-hover)',
    color: root ? '#fff' : depth === 1 ? 'var(--accent)' : 'var(--text-primary)',
    borderBottom: node.members.length > 0 || onAdd ? '1px solid var(--border)' : 'none',
    cursor: onEdit ? 'pointer' : 'default',
  };

  const header = (
    <>
      <span style={{ minWidth: 0 }}>{node.name}</span>
      {node.members.length > 0 && (
        <span style={{ fontSize: '11px', fontWeight: 500, opacity: root ? 0.85 : 0.7, flexShrink: 0 }}>
          {node.members.length}
        </span>
      )}
      {onEdit && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.6 }} aria-hidden>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      )}
    </>
  );

  return (
    <div
      data-org-node={node.id}
      style={{
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        width: `${NODE_W}px`,
        height: `${h}px`,
        borderRadius: '10px',
        border: `1px solid ${root ? 'var(--accent)' : 'var(--border)'}`,
        background: 'var(--bg-card)',
        boxShadow: moving ? '0 10px 28px rgba(15, 23, 42, 0.22)' : ORG_CARD_SHADOW,
        overflow: 'hidden',
        zIndex: moving ? 2 : undefined,
      }}
    >
      <div style={{ position: 'relative' }}>
        {onEdit ? (
          <button type="button" onClick={onEdit} title="이름 · 상위 부서 · 순서 고치기" style={{ ...headerStyle, border: 'none' }}>
            {header}
          </button>
        ) : (
          <div style={headerStyle}>{header}</div>
        )}
        {moveHandlers && (
          /*
            화살표 손잡이 — 연필 옆(사용자 지시 2026-10-01). 누른 채 끌면 칸이 따라온다. 머리칸 단추
            위에 겹쳐 두어야 「연필 옆」 이 되고, 머리칸 클릭(고치기)과 섞이지 않는다.
          */
          <button
            type="button"
            title="끌어서 자리 옮기기"
            aria-label="끌어서 자리 옮기기"
            {...moveHandlers}
            style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'inherit',
              opacity: 0.75,
              cursor: moving ? 'grabbing' : 'grab',
              touchAction: 'none',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" />
            </svg>
          </button>
        )}
      </div>

      {node.members.length > 0 && (
        <div style={{ padding: `${BODY_PAD / 2}px 5px` }}>
          {node.members.map((member) => {
            const active = member.personnelId === selectedId;
            return (
              <button
                key={member.personnelId}
                type="button"
                onClick={() => onPick(member.personnelId)}
                aria-current={active ? 'true' : undefined}
                className="org-tree-member"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  width: '100%',
                  height: `${ROW_H}px`,
                  padding: '0 5px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  textAlign: 'left',
                  background: active ? 'var(--accent-bg)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--text-primary)',
                  overflow: 'hidden',
                }}
              >
                <Avatar name={member.name} size={16} photoUrl={member.hasPhoto ? directoryPhotoUrl(member.personnelId) : null} />
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {member.name}
                </span>
                {member.rank && (
                  <span style={{ fontSize: '11px', color: active ? 'var(--accent)' : 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {member.rank}
                  </span>
                )}
                {member.teamLead && <span style={{ ...leadBadge, marginLeft: 'auto' }}>팀장</span>}
                <AbsenceBadge personnelId={member.personnelId} style={member.teamLead ? undefined : { marginLeft: 'auto' }} />
              </button>
            );
          })}
        </div>
      )}

      {onAdd && (
        /* 현장 판의 + 와 같은 줄. 누르면 이 부서에 넣을 사람을 고른다 — 권한은 눌렀을 때 서버가 답한다. */
        <button
          type="button"
          onClick={onAdd}
          title="이 부서에 사람 넣기"
          className="org-tree-member"
          style={{
            display: 'block',
            width: '100%',
            height: `${ADD_ROW_H}px`,
            fontSize: '12px',
            lineHeight: 1,
            color: 'var(--text-muted)',
          }}
        >
          +
        </button>
      )}
    </div>
  );
}

// ── 숲 세우기 ────────────────────────────────────────────────────────────────

/**
 * 평면 목록을 숲으로. 부모가 목록에 없거나 <b>위로 올라가다 자기를 다시 만나는</b> 칸은 뿌리로 세운다 —
 * 서버가 순환을 막지만, 막기 전에 들어간 자료가 있어도 조직도가 통째로 사라지면 안 된다.
 */
function buildForest(nodes: OrgTreeNode[], prune: boolean): Tree[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));

  const inCycle = (node: OrgTreeNode): boolean => {
    const seen = new Set<string>([node.id]);
    let cursor = node.parentId;
    while (cursor !== null) {
      if (seen.has(cursor)) return true;
      seen.add(cursor);
      cursor = byId.get(cursor)?.parentId ?? null;
    }
    return false;
  };

  const childrenOf = new Map<string, OrgTreeNode[]>();
  const roots: OrgTreeNode[] = [];
  for (const node of nodes) {
    const parent = node.parentId !== null && byId.has(node.parentId) && !inCycle(node) ? node.parentId : null;
    if (parent === null) roots.push(node);
    else childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), node]);
  }

  const build = (node: OrgTreeNode): Tree => ({
    node,
    children: (childrenOf.get(node.id) ?? []).map(build),
  });

  const keep = (tree: Tree): Tree | null => {
    const children = tree.children.map(keep).filter((child): child is Tree => child !== null);
    if (children.length === 0 && tree.node.members.length === 0) return null;
    return { node: tree.node, children };
  };

  const forest = roots.map(build);
  return prune ? forest.map(keep).filter((tree): tree is Tree => tree !== null) : forest;
}

// ── 좌표 ─────────────────────────────────────────────────────────────────────

function headerLines(name: string): number {
  return Math.max(1, Math.ceil(name.length / HEADER_CHARS_PER_LINE));
}

function nodeHeight(node: OrgTreeNode, addRow: boolean): number {
  const body = node.members.length > 0 ? node.members.length * ROW_H + BODY_PAD : EMPTY_BODY_H;
  const extra = addRow && node.editable !== false ? ADD_ROW_H : 0;
  return headerLines(node.name) * HEADER_LINE + HEADER_PAD + body + extra;
}

function isStack(tree: Tree): boolean {
  return tree.children.length >= STACK_MIN && tree.children.every((child) => child.children.length === 0);
}

function layout(forest: Tree[], addRow: boolean): { placed: Placed[]; links: string[]; width: number; height: number } {
  const heightOf = (node: OrgTreeNode) => nodeHeight(node, addRow);
  const widths = new Map<Tree, number>();
  const widthOf = (tree: Tree): number => {
    const known = widths.get(tree);
    if (known !== undefined) return known;
    let width: number;
    if (isStack(tree)) width = NODE_W * 2 + STACK_COL_GAP;
    else if (tree.children.length === 0) width = NODE_W;
    else {
      const span = tree.children.reduce((sum, child) => sum + widthOf(child), 0) + H_GAP * (tree.children.length - 1);
      width = Math.max(NODE_W, span);
    }
    widths.set(tree, width);
    return width;
  };

  /*
    줄마다 가장 높은 칸으로 줄 높이를 잡는다 — 같은 줄의 칸은 위가 나란해야 조직도로 읽힌다.
    두 열로 내린 가지는 자기 줄에 세지 않는다. 그 아래로 아무것도 안 오므로 남의 줄을 밀 이유가 없다.
  */
  const levelH: number[] = [];
  const measureLevels = (tree: Tree, depth: number) => {
    levelH[depth] = Math.max(levelH[depth] ?? 0, heightOf(tree.node));
    if (isStack(tree)) return;
    for (const child of tree.children) measureLevels(child, depth + 1);
  };
  for (const tree of forest) measureLevels(tree, 0);

  const levelY: number[] = [];
  for (let depth = 0; depth < levelH.length; depth += 1) {
    levelY[depth] = depth === 0 ? 0 : (levelY[depth - 1] ?? 0) + (levelH[depth - 1] ?? 0) + V_GAP;
  }

  const placed: Placed[] = [];
  /*
    선은 자리를 다 잡은 <b>뒤에</b> 긋는다. 자리를 잡으면서 바로 그으면 손으로 옮긴 칸(offset)이
    선을 두고 떠난다 — 어떤 선을 누구와 누구 사이에 그을지만 적어 두고, 좌표는 마지막에 읽는다.
  */
  const wires: Array<{ kind: 'elbow'; parent: string; child: string } | { kind: 'stack'; parent: string; children: string[]; centered: string | null }> = [];
  let bottom = 0;

  const place = (tree: Tree, depth: number, left: number, top: number) => {
    const width = widthOf(tree);
    const h = heightOf(tree.node);
    const x = left + (width - NODE_W) / 2;
    const y = (levelY[depth] ?? 0) + top;
    placed.push({ node: tree.node, depth, x, y, h });
    bottom = Math.max(bottom, y + h);

    const cx = x + NODE_W / 2;
    const py = y + h;

    if (isStack(tree)) {
      const leftX = cx - STACK_COL_GAP / 2 - NODE_W;
      const rightX = cx + STACK_COL_GAP / 2;
      let rowY = py + STACK_TOP_GAP;
      let centered: string | null = null;

      for (let index = 0; index < tree.children.length; index += 2) {
        const pair = tree.children.slice(index, index + 2);
        const rowH = Math.max(...pair.map((child) => heightOf(child.node)));

        if (pair.length === 2) {
          const [first, second] = pair as [Tree, Tree];
          placed.push({ node: first.node, depth: depth + 1, x: leftX, y: rowY, h: heightOf(first.node) });
          placed.push({ node: second.node, depth: depth + 1, x: rightX, y: rowY, h: heightOf(second.node) });
        } else {
          // 홀수의 마지막 — 척추 끝에 가운데로 선다.
          const [last] = pair as [Tree];
          placed.push({ node: last.node, depth: depth + 1, x: cx - NODE_W / 2, y: rowY, h: heightOf(last.node) });
          centered = last.node.id;
        }

        bottom = Math.max(bottom, rowY + rowH);
        rowY += rowH + STACK_ROW_GAP;
      }

      wires.push({ kind: 'stack', parent: tree.node.id, children: tree.children.map((child) => child.node.id), centered });
      return;
    }

    if (tree.children.length === 0) return;

    const span = tree.children.reduce((sum, child) => sum + widthOf(child), 0) + H_GAP * (tree.children.length - 1);
    let cursor = left + (width - span) / 2;
    for (const child of tree.children) {
      place(child, depth + 1, cursor, top);
      wires.push({ kind: 'elbow', parent: tree.node.id, child: child.node.id });
      cursor += widthOf(child) + H_GAP;
    }
  };

  /*
    세 무리로 세운다 (사용자 지시 2026-10-01).
      TOP  — 맨 윗줄 왼쪽에 독립해서(감사실). 현장 판의 총괄소장과 같은 자리다.
      조직 — 가운데. 뿌리가 여럿이면 한 줄에 다 세우지 않고 줄 폭을 넘으면 다음 줄로 내린다
             (「좌우로 드래그 해야 해?」 — 양식 밖 부서 열 그루가 옆으로 늘어서 판이 화면 세 배였다).
      SIDE — 오른쪽 구석에 세로로 쌓는다(현장준비팀 · 소속 미지정).
    그 위에 맞춤 축소({@code OrgTreeChart} 의 scale)가 한 번 더 걸린다.
  */
  const tops = forest.filter((tree) => tree.node.slot === 'TOP');
  const sides = forest.filter((tree) => tree.node.slot === 'SIDE');
  const mains = forest.filter((tree) => tree.node.slot !== 'TOP' && tree.node.slot !== 'SIDE');

  let cursor = 0;
  for (const tree of tops) {
    place(tree, 0, cursor, 0);
    cursor += widthOf(tree) + FOREST_GAP;
  }

  let top = 0;
  let width = cursor;
  const mainLeft = cursor;
  for (const tree of mains) {
    const treeWidth = widthOf(tree);
    if (cursor > mainLeft && cursor + treeWidth > mainLeft + FOREST_ROW_MAX) {
      top = bottom + FOREST_GAP;
      cursor = mainLeft;
    }
    place(tree, 0, cursor, top);
    cursor += treeWidth + FOREST_GAP;
    width = Math.max(width, cursor - FOREST_GAP);
  }

  if (sides.length > 0) {
    const sideLeft = width > 0 ? width + FOREST_GAP : 0;
    let sideTop = 0;
    for (const tree of sides) {
      const before = bottom;
      bottom = 0;
      place(tree, 0, sideLeft, sideTop);
      sideTop = bottom + SIDE_GAP;
      bottom = Math.max(before, bottom);
      width = Math.max(width, sideLeft + widthOf(tree));
    }
  }

  /*
    손으로 옮긴 거리(V391)를 얹는다 — 칸과 <b>그 아래 가지 전부</b>에. 부모만 옮기고 자식이 남으면
    그 가지는 뿌리에서 떨어져 나간 것처럼 보인다. 위에서 내려오며 더하므로 조상의 거리가 누적된다.
  */
  const byId = new Map(placed.map((item) => [item.node.id, item]));
  const shift = (tree: Tree, dx: number, dy: number) => {
    const sumDx = dx + (tree.node.offset?.dx ?? 0);
    const sumDy = dy + (tree.node.offset?.dy ?? 0);
    const item = byId.get(tree.node.id);
    if (item && (sumDx !== 0 || sumDy !== 0)) {
      item.x += sumDx;
      item.y += sumDy;
    }
    for (const child of tree.children) shift(child, sumDx, sumDy);
  };
  for (const tree of forest) shift(tree, 0, 0);

  // 왼쪽·위로 끌려 나간 만큼 판을 밀어 음수 좌표가 없게 한다. 선도 같은 좌표에서 긋는다.
  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;
  for (const item of placed) {
    minX = Math.min(minX, item.x);
    minY = Math.min(minY, item.y);
    maxX = Math.max(maxX, item.x + NODE_W);
    maxY = Math.max(maxY, item.y + item.h);
  }
  if (minX < 0 || minY < 0) {
    for (const item of placed) {
      item.x -= minX;
      item.y -= minY;
    }
  }

  const links: string[] = [];
  for (const wire of wires) {
    const parent = byId.get(wire.parent);
    if (!parent) continue;
    const px = parent.x + NODE_W / 2;
    const py = parent.y + parent.h;

    if (wire.kind === 'elbow') {
      const child = byId.get(wire.child);
      if (!child) continue;
      const cx = child.x + NODE_W / 2;
      const cy = child.y;
      links.push(elbowPath(px, py, cx, cy, cy - V_GAP / 2));
      continue;
    }

    // 척추 — 아래로 가장 멀리 간 자식까지. 자식은 척추에 가까운 쪽 모서리로 가로선을 받는다.
    let spineEnd = py;
    for (const id of wire.children) {
      const child = byId.get(id);
      if (!child) continue;
      if (id === wire.centered) {
        links.push(elbowPath(px, py, child.x + NODE_W / 2, child.y, child.y - STACK_TOP_GAP / 2));
        spineEnd = Math.max(spineEnd, child.y - STACK_TOP_GAP / 2);
        continue;
      }
      const attachY = child.y + (headerLines(child.node.name) * HEADER_LINE + HEADER_PAD) / 2;
      const edgeX = child.x + NODE_W / 2 < px ? child.x + NODE_W : child.x;
      links.push(`M ${px} ${attachY} H ${edgeX}`);
      spineEnd = Math.max(spineEnd, attachY);
    }
    links.push(`M ${px} ${py} V ${spineEnd}`);
  }

  return { placed, links, width: Math.max(width, maxX - Math.min(minX, 0)), height: Math.max(bottom, maxY - Math.min(minY, 0)) };
}
